import { defineMessages, useIntl } from 'react-intl';
import {
  SocialNetworksProvider,
  useSocialNetworksContext,
} from '../../SocialNetworksProvider/SocialNetworksProvider';
import SocialLinks from '../../SocialLinks/SocialLinks';
import {
  isExternalHref,
  resolveImageSrcSet,
  resolveImageUrl,
  resolveLinkHref,
  type ImageFieldValue,
  type LinkFieldValue,
} from '../../../helpers/blocks';
import heroDefault from '../../../assets/hero-default.jpg';
import heroDefault768 from '../../../assets/hero-default-768.jpg';

// A foto do hero é o LCP da home: `<img>` (e não background-image) para o
// navegador poder priorizar o download e escolher o tamanho pelo viewport.
const HERO_SCALES = ['large', 'great', 'huge'];
const HERO_SIZES = '100vw';
const HERO_DEFAULT_SRCSET = `${heroDefault768} 768w, ${heroDefault} 1440w`;

const messages = defineMessages({
  defaultTitle: {
    id: 'hero.default.title',
    defaultMessage: 'The 2027 Ocean Decade Conference',
  },
  defaultSubtitle: {
    id: 'hero.default.subtitle',
    defaultMessage: 'Accelerating Ocean Solutions to 2030 and Beyond',
  },
  followLabel: {
    id: 'hero.followLabel',
    defaultMessage: 'Follow the Ocean Decade on social media',
  },
});

export type InfoItem = { label?: string; value?: string };

export type HeroBannerData = {
  title?: string;
  subtitle?: string;
  infoItems?: InfoItem[];
  ctaLabel?: string;
  ctaLink?: LinkFieldValue;
  backgroundImage?: ImageFieldValue;
  // Campos legados (schema antigo) — mantidos para não quebrar conteúdo salvo.
  locationLabel?: string;
  locationValue?: string;
  date1Label?: string;
  date1Value?: string;
  date2Label?: string;
  date2Value?: string;
};

/** Compatibilidade: conteúdo salvo com o schema antigo (6 campos fixos). */
const legacyInfoItems = (data: HeroBannerData): InfoItem[] =>
  [
    { label: data.locationLabel, value: data.locationValue },
    { label: data.date1Label, value: data.date1Value },
    { label: data.date2Label, value: data.date2Value },
  ].filter((item) => item.label || item.value);

/**
 * O mockup destaca o ano dentro do título ("The 2027") em peso light e um
 * degrau acima (up-08/300 vs up-07/700). O título continua um único campo
 * editável; o destaque é só apresentação.
 */
const renderTitle = (title: string) => {
  const match = title.match(/\b(20\d{2})\b/);
  if (!match || match.index === undefined) return title;
  const start = match.index;
  const end = start + match[1].length;
  return (
    <>
      {title.slice(0, start)}
      <span className="hero-banner__title-year">{match[1]}</span>
      {title.slice(end)}
    </>
  );
};

/**
 * Faixa social abaixo do box (mockup): label + ícones das redes cadastradas
 * no painel de controle (endpoint @odcsite). Sem redes cadastradas, não
 * renderiza nada — decisão: sem fallback hardcoded.
 */
const HeroSocialRow = () => {
  const intl = useIntl();
  const { networks, loaded, error } = useSocialNetworksContext();

  if (!loaded || error || networks.length === 0) {
    return null;
  }

  return (
    <div className="hero-banner__social">
      <span className="hero-banner__social-label">
        {intl.formatMessage(messages.followLabel)}
      </span>
      <SocialLinks iconSize="16px" />
    </div>
  );
};

type HeroBannerBodyProps = {
  data: HeroBannerData;
  isEditMode?: boolean;
};

const HeroBannerBody = ({ data, isEditMode }: HeroBannerBodyProps) => {
  const intl = useIntl();

  const infoItems: InfoItem[] = (
    data.infoItems?.length ? data.infoItems : legacyInfoItems(data)
  ).filter((item) => item.label || item.value);

  const cmsImageUrl = resolveImageUrl(data.backgroundImage);
  const imageUrl = cmsImageUrl || heroDefault;
  const imageSrcSet = cmsImageUrl
    ? resolveImageSrcSet(data.backgroundImage, HERO_SCALES)
    : HERO_DEFAULT_SRCSET;
  const ctaHref = resolveLinkHref(data.ctaLink);
  // Sem link o botão não aparece no site (nada de `href="#"` — foi o que a
  // UNESCO reportou); no editor continua visível para o editor perceber que
  // falta preencher o link.
  const showCta = Boolean(data.ctaLabel && (ctaHref || isEditMode));

  return (
    <div className="block-hero-banner">
      <div className="hero-banner__media">
        {/* Decorativa (alt vazio): o título do bloco já diz do que se trata.
            `fetchpriority` em minúsculas: React 18 só repassa a versão DOM. */}
        <img
          className="hero-banner__image"
          src={imageUrl}
          srcSet={imageSrcSet}
          sizes={imageSrcSet && HERO_SIZES}
          alt=""
          decoding="async"
          // @ts-expect-error — atributo DOM ainda sem tipagem no React 18
          fetchpriority="high"
        />
        {/* Overlay escuro sobre a foto — ver token --hero-overlay-dark */}
        <span className="hero-banner__overlay hero-banner__overlay--dark" />
      </div>

      <div className="hero-banner__box">
        <div className="hero-banner__intro">
          <h1 className="hero-banner__title">
            {renderTitle(
              data.title || intl.formatMessage(messages.defaultTitle),
            )}
          </h1>
          <p className="hero-banner__subtitle">
            {data.subtitle || intl.formatMessage(messages.defaultSubtitle)}
          </p>
        </div>

        {(infoItems.length > 0 || showCta) && (
          <div className="hero-banner__meta">
            {infoItems.length > 0 && (
              <ul className="hero-banner__info">
                {infoItems.map((item, i) => (
                  <li className="hero-banner__info-item" key={i}>
                    {item.label && (
                      <span className="hero-banner__info-label">
                        {item.label}
                      </span>
                    )}
                    {item.value && (
                      <span className="hero-banner__info-value">
                        {item.value}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {showCta &&
              (isEditMode ? (
                // No editor o link não navega: o clique seleciona o bloco.
                <span className="btn btn--accent hero-banner__cta">
                  {data.ctaLabel}
                </span>
              ) : (
                <a
                  className="btn btn--accent hero-banner__cta"
                  href={ctaHref}
                  {...(isExternalHref(ctaHref) && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                >
                  {data.ctaLabel}
                </a>
              ))}
          </div>
        )}
      </div>

      <SocialNetworksProvider>
        <HeroSocialRow />
      </SocialNetworksProvider>
    </div>
  );
};

export default HeroBannerBody;
